import React, { useEffect, useState, useCallback, useRef } from "react";
import SurveyListHeader from "../../../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../../../component/common/layout/SurveyListSidebar";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useVerifySurveyId from "../../../../../hooks/verifySurveyId";
import { getCookiePolicy, saveCookiePolicy, toggleCookiePolicy } from "../../../../../store/actions/surveyAction";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { useTranslation } from "react-i18next";

export default function CookiePolicyPage() {
  const survey_id = useVerifySurveyId();
  const { survey_name } = useSelector((state) => state.permissionsData);
  const dispatch = useDispatch();
  const { cookiePolicy, cookiePolicyLoading } = useSelector((state) => state.surveyData);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [acceptBtnEnabled, setAcceptBtnEnabled] = useState(true);
  const [acceptBtnText, setAcceptBtnText] = useState('');
  const [rejectBtnEnabled, setRejectBtnEnabled] = useState(true);
  const [rejectBtnText, setRejectBtnText] = useState('');
  const { t } = useTranslation();
  const colorInputRef = useRef(null);
  const [currentColor, setCurrentColor] = useState('#000000');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        bulletList: true,
        orderedList: true,
      }),
      Underline,
      LinkExtension.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: "",
    autofocus: false,
    editable: window.modulePermissions.M20.edit == 1,
  });

  useEffect(() => {
    if (survey_id) {
      dispatch(getCookiePolicy(survey_id));
    }
  }, [survey_id, dispatch]);

  useEffect(() => {
    if (editor) {
      if (cookiePolicy && cookiePolicy.cookie_content) {
        editor.commands.setContent(cookiePolicy.cookie_content);
      } else {
        editor.commands.setContent("");
      }
    }
    if (cookiePolicy) {
      setIsEnabled(cookiePolicy.is_enabled === 1);
      setAcceptBtnEnabled(cookiePolicy.accept_btn_enabled !== 0);
      setAcceptBtnText(cookiePolicy.accept_btn_text || '');
      setRejectBtnEnabled(cookiePolicy.reject_btn_enabled !== 0);
      setRejectBtnText(cookiePolicy.reject_btn_text || '');
    } else {
      setIsEnabled(false);
      setAcceptBtnEnabled(true);
      setAcceptBtnText('');
      setRejectBtnEnabled(true);
      setRejectBtnText('');
    }
  }, [editor, cookiePolicy]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt(t('settings.editor.url_prompt'), previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor, t]);

  const onSave = async () => {
    if (!editor || !survey_id) return;
    setSaving(true);
    const html = editor.getHTML();
    dispatch(saveCookiePolicy(survey_id, html, isEnabled, {
      accept_btn_enabled: acceptBtnEnabled,
      accept_btn_text: acceptBtnText,
      reject_btn_enabled: rejectBtnEnabled,
      reject_btn_text: rejectBtnText,
    }, () => setSaving(false)));
  };

  const handleToggle = () => {
    const newVal = !isEnabled;
    setIsEnabled(newVal);
    if (survey_id) {
      dispatch(toggleCookiePolicy(survey_id, newVal));
    }
  };

  return (
    <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
      <SurveyListHeader />
      <div id="viewport" className="flex-grow-1 d-flex survey-main-wrapper bg-white">
        <SurveyListSidebar />
        <div className="flex-grow-1 page-content-wrap d-flex flex-column">
          <div className="page-heading d-flex align-items-center">
            <div className="flex-grow-1">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/survey/list">{t("survey.title")}</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`/admin/survey/create/qa-builder?survey_id=${survey_id}`}>{survey_name}</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    <strong>{t("survey.cookie_policy_page.breadcrumb")}</strong>
                  </li>
                </ol>
              </nav>
              <h1>{t("survey.cookie_policy_page.page_title")}</h1>
              <p className="text-muted mb-0">{t("survey.cookie_policy_page.page_description")}</p>
            </div>
          </div>

          <div className="flex-grow-1">
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0">{t("survey.cookie_policy_page.show_in_footer")}</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      onClick={window.modulePermissions.M20.edit == 1 ? (cookiePolicyLoading ? undefined : handleToggle) : undefined}
                      style={{
                        width: '48px', height: '26px', borderRadius: '13px',
                        cursor: (window.modulePermissions.M20.edit != 1 || cookiePolicyLoading) ? 'not-allowed' : 'pointer',
                        background: isEnabled ? '#E60000' : '#ccc',
                        position: 'relative', transition: 'background 0.25s',
                      }}
                    >
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '2px', left: isEnabled ? '24px' : '2px',
                        transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      }} />
                    </div>
                    <span style={{ fontWeight: 500, color: isEnabled ? '#E60000' : '#999', userSelect: 'none' }}>
                      {isEnabled ? t("survey.cookie_policy_page.enabled") : t("survey.cookie_policy_page.disabled")}
                    </span>
                  </div>
                </div>
                <hr />
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleBold().run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.bold')}</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleItalic().run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.italic')}</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleUnderline().run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.underline')}</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 1 }).run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>H1</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>H2</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 3 }).run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>H3</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleBulletList().run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.bulleted_list')}</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleOrderedList().run()} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.numbered_list')}</button>
                  <button className="btn btn-bordered" onClick={setLink} disabled={!editor || window.modulePermissions.M20.edit != 1}>{t('settings.editor.link')}</button>
                  <button
                    className="btn btn-bordered"
                    onClick={() => editor && editor.chain().focus().insertTable({ rows: 3, cols: 6, withHeaderRow: true }).run()}
                    disabled={!editor || window.modulePermissions.M20.edit != 1}
                  >
                    {t('settings.editor.table')}
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      className="btn btn-bordered"
                      disabled={!editor || window.modulePermissions.M20.edit != 1}
                      onClick={() => colorInputRef.current && colorInputRef.current.click()}
                      title={t('settings.editor.text_color')}
                    >
                      <span style={{ borderBottom: `3px solid ${currentColor}`, paddingBottom: 1 }}>A</span> {t('settings.editor.color')}
                    </button>
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={currentColor}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                      onChange={(e) => {
                        const color = e.target.value;
                        setCurrentColor(color);
                        if (editor) editor.chain().focus().setColor(color).run();
                      }}
                      disabled={window.modulePermissions.M20.edit != 1}
                    />
                  </div>
                  <button
                    className="btn btn-bordered"
                    disabled={!editor || window.modulePermissions.M20.edit != 1}
                    onClick={() => {
                      if (editor) {
                        editor.chain().focus().unsetColor().run();
                        setCurrentColor('#000000');
                      }
                    }}
                    title={t('settings.editor.no_color')}
                  >
                    {t('settings.editor.no_color')}
                  </button>
                </div>
                <div className="editor-wrap privacy-editor">
                  <EditorContent editor={editor} />
                </div>
                <style>{`
                  .privacy-editor .ProseMirror table { width: 100%; border-collapse: collapse; }
                  .privacy-editor .ProseMirror th, .privacy-editor .ProseMirror td { border: 1px solid #000; padding: 8px; }
                  .privacy-editor .ProseMirror thead th { background: #f2f2f2; font-weight: 600; }
                `}</style>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h5 className="mb-3">{t("survey.cookie_policy_page.banner_settings")}</h5>

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontWeight: 500 }}>{t("survey.cookie_policy_page.accept_button")}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      onClick={window.modulePermissions.M20.edit == 1 ? () => setAcceptBtnEnabled(!acceptBtnEnabled) : undefined}
                      style={{
                        width: '48px', height: '26px', borderRadius: '13px',
                        cursor: window.modulePermissions.M20.edit != 1 ? 'not-allowed' : 'pointer',
                        background: acceptBtnEnabled ? '#E60000' : '#ccc',
                        position: 'relative', transition: 'background 0.25s',
                      }}
                    >
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '2px', left: acceptBtnEnabled ? '24px' : '2px',
                        transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      }} />
                    </div>
                    <span style={{ fontWeight: 500, color: acceptBtnEnabled ? '#E60000' : '#999', userSelect: 'none', minWidth: 60 }}>
                      {acceptBtnEnabled ? t("survey.cookie_policy_page.show") : t("survey.cookie_policy_page.hidden")}
                    </span>
                  </div>
                </div>
                {acceptBtnEnabled && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, color: '#666' }}>{t("survey.cookie_policy_page.button_text_label")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("survey.cookie_policy_page.accept_placeholder")}
                      value={acceptBtnText}
                      onChange={window.modulePermissions.M20.edit == 1 ? (e) => setAcceptBtnText(e.target.value) : undefined}
                      readOnly={window.modulePermissions.M20.edit != 1}
                    />
                  </div>
                )}

                <hr />

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontWeight: 500 }}>{t("survey.cookie_policy_page.reject_button")}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      onClick={window.modulePermissions.M20.edit == 1 ? () => setRejectBtnEnabled(!rejectBtnEnabled) : undefined}
                      style={{
                        width: '48px', height: '26px', borderRadius: '13px',
                        cursor: window.modulePermissions.M20.edit != 1 ? 'not-allowed' : 'pointer',
                        background: rejectBtnEnabled ? '#E60000' : '#ccc',
                        position: 'relative', transition: 'background 0.25s',
                      }}
                    >
                      <div style={{
                        width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '2px', left: rejectBtnEnabled ? '24px' : '2px',
                        transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      }} />
                    </div>
                    <span style={{ fontWeight: 500, color: rejectBtnEnabled ? '#E60000' : '#999', userSelect: 'none', minWidth: 60 }}>
                      {rejectBtnEnabled ? t("survey.cookie_policy_page.show") : t("survey.cookie_policy_page.hidden")}
                    </span>
                  </div>
                </div>
                {rejectBtnEnabled && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 13, color: '#666' }}>{t("survey.cookie_policy_page.button_text_label")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("survey.cookie_policy_page.reject_placeholder")}
                      value={rejectBtnText}
                      onChange={window.modulePermissions.M20.edit == 1 ? (e) => setRejectBtnText(e.target.value) : undefined}
                      readOnly={window.modulePermissions.M20.edit != 1}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="button-wrap d-flex justify-content-end">
              {window.modulePermissions.M20.edit == 1 && (<button className="btn btn-primary" onClick={onSave} disabled={saving || cookiePolicyLoading || !editor}>{(saving || cookiePolicyLoading) ? t("survey.cookie_policy_page.saving") : t("survey.cookie_policy_page.save")}</button>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
